// external
import React, { FC, useCallback, useState } from 'react';
import { Box, Typography, Collapse, IconButton, Tooltip, CircularProgress, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';

// internal
import { palette } from '../../theme';
import { parseRaceDateTime, formatDuration } from '../../helpers/general.helpers';
import type { Race, RaceLogEntry } from '../../models/race.models';
import type { UpdateRaceLogPayload, CreateTeamRaceLogsPayload } from '../../models/race.models';
import RaceLogEntryRow, { ROW_GRID } from './race-log-entry-row';
import CreateTeamRaceLogsModal from './create-team-race-logs-modal';

const CHIP_PALETTE = [
  { bg: '#E3F2FD', color: '#1565C0' },
  { bg: '#FFF8E1', color: '#E65100' },
  { bg: '#E8F5E9', color: '#2E7D32' },
  { bg: '#F3E5F5', color: '#6A1B9A' },
  { bg: '#FBE9E7', color: '#BF360C' },
];

function getSportChipColor(label: string) {
  let h = 0;
  for (const c of label) h = c.charCodeAt(0) + ((h << 5) - h);
  return CHIP_PALETTE[Math.abs(h) % CHIP_PALETTE.length];
}

function getAccentBarColor(race: Race, entries: RaceLogEntry[]): string {
  const places = entries.map((e) => e.result).filter((r) => Number.isFinite(r));
  if (places.some((p) => p === 1)) return palette.success.main;
  if (places.some((p) => p === 2 || p === 3)) return palette.warning.main;
  if (race.sport_type?.toLowerCase().includes('classic')) return palette.purple.main;
  return palette.primary.main;
}

function getBestPodiumPlace(entries: RaceLogEntry[]): 1 | 2 | 3 | null {
  const places = entries.map((e) => e.result).filter((r): r is number => Number.isFinite(r) && r >= 1 && r <= 3);
  if (places.length === 0) return null;
  return Math.min(...places) as 1 | 2 | 3;
}

const PODIUM_BADGE_STYLES: Record<1 | 2 | 3, { label: string; color: string; bg: string }> = {
  1: { label: '1st', color: palette.success.dark, bg: palette.success.light },
  2: { label: '2nd', color: palette.warning.dark, bg: palette.warning.light },
  3: { label: '3rd', color: palette.warning.dark, bg: palette.warning.light },
};

export type RaceEvent = Race;

interface RaceEventCardProps {
  race: Race;
  expanded: boolean;
  onToggle: () => void;
  entries?: RaceLogEntry[];
  entriesLoading?: boolean;
  onEntriesChange?: (entries: RaceLogEntry[]) => void;
  teamId?: string;
  onAddTeamRaceLog?: (race: Race, payload: CreateTeamRaceLogsPayload) => Promise<void>;
  onEditEntry?: (entryId: number, payload: UpdateRaceLogPayload) => Promise<void>;
  onDeleteEntry?: (entryId: number) => Promise<void>;
  onOpenTraining?: (trainingId: number, viewerType: 'athlete' | 'coach' | 'guardian') => void;
  isCoach?: boolean;
  isGuardian?: boolean;
}

function formatDurationDisplay(duration: string | number | undefined): string {
  if (duration === undefined || duration === null) return '—';
  if (typeof duration === 'number') return formatDuration(duration);
  return duration;
}

const RaceEventCard: FC<RaceEventCardProps> = ({
  race,
  expanded,
  onToggle,
  entries = [],
  entriesLoading,
  onEntriesChange,
  teamId,
  onAddTeamRaceLog,
  onEditEntry,
  onDeleteEntry,
  onOpenTraining,
  isCoach,
  isGuardian,
}) => {
  const { dateDisplay, timeDisplay } = parseRaceDateTime(race.date);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [hovered, setHovered] = useState(false);

  const handleAddClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAddModalOpen(true);
  };

  const handleEditEntry = useCallback(
    async (entryId: number, payload: UpdateRaceLogPayload) => {
      if (onEditEntry) {
        await onEditEntry(entryId, payload);
      } else {
        const next = entries.map((e) => (e.id === entryId ? { ...e, ...payload } : e));
        onEntriesChange?.(next);
      }
    },
    [entries, onEntriesChange, onEditEntry]
  );

  const handleDeleteEntry = useCallback(
    async (entryId: number) => {
      if (onDeleteEntry) {
        await onDeleteEntry(entryId);
      } else {
        const next = entries.filter((e) => e.id !== entryId);
        onEntriesChange?.(next);
      }
    },
    [entries, onEntriesChange, onDeleteEntry]
  );

  const handleEventClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onOpenTraining) return;
    const viewerType: 'athlete' | 'coach' | 'guardian' = isCoach ? 'coach' : isGuardian ? 'guardian' : 'athlete';
    onOpenTraining(race.id, viewerType);
  };

  const sportChip = getSportChipColor(race.sport ?? '');
  const sportTypeChip = getSportChipColor(race.sport_type ?? '');
  const accentColor = getAccentBarColor(race, entries);
  const podiumPlace = getBestPodiumPlace(entries);
  const podiumBadge = podiumPlace != null ? PODIUM_BADGE_STYLES[podiumPlace] : null;
  const metaParts = [dateDisplay, timeDisplay, race.location].filter(Boolean);
  const athleteCount = entries.length;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        backgroundColor: palette.background.paper,
        borderRadius: '16px',
        border: `1px solid ${palette.grey[200]}`,
        boxShadow: hovered ? `0 4px 16px ${alpha(palette.grey[900], 0.07)}` : 'none',
        transition: 'box-shadow 0.15s ease',
        overflow: 'hidden',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Box
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        aria-expanded={expanded}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.75,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <Box
          sx={{
            width: 4,
            height: 48,
            borderRadius: 1,
            flexShrink: 0,
            backgroundColor: accentColor,
          }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.25 }}>
            <Typography
              variant="subtitle1"
              onClick={onOpenTraining ? handleEventClick : undefined}
              sx={{
                fontWeight: 700,
                color: palette.text.primary,
                cursor: onOpenTraining ? 'pointer' : 'inherit',
                '&:hover': {
                  textDecoration: onOpenTraining ? 'underline' : 'none',
                  color: onOpenTraining ? palette.primary.main : undefined,
                },
              }}
            >
              {race.name}
            </Typography>
            {podiumBadge ? (
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  px: 1,
                  py: 0.25,
                  borderRadius: '8px',
                  fontSize: 11,
                  fontWeight: 700,
                  lineHeight: 1.4,
                  color: podiumBadge.color,
                  backgroundColor: podiumBadge.bg,
                }}
              >
                {podiumBadge.label}
              </Box>
            ) : null}
          </Box>
          <Typography variant="body2" sx={{ color: palette.text.secondary }}>
            {metaParts.join(' • ')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
            {race.sport ? (
              <Chip
                label={race.sport}
                size="small"
                sx={{
                  backgroundColor: sportChip.bg,
                  color: sportChip.color,
                  fontWeight: 600,
                  border: 'none',
                  height: 24,
                }}
              />
            ) : null}
            {race.sport_type ? (
              <Chip
                label={race.sport_type}
                size="small"
                sx={{
                  backgroundColor: sportTypeChip.bg,
                  color: sportTypeChip.color,
                  fontWeight: 600,
                  border: 'none',
                  height: 24,
                }}
              />
            ) : null}
            {athleteCount > 0 ? (
              <Chip
                label={`${athleteCount} athlete${athleteCount === 1 ? '' : 's'}`}
                size="small"
                sx={{
                  backgroundColor: palette.grey[100],
                  color: palette.text.secondary,
                  fontWeight: 600,
                  border: 'none',
                  height: 24,
                }}
              />
            ) : null}
          </Box>
        </Box>

        <Box sx={{ textAlign: 'right', flexShrink: 0, minWidth: 72, mr: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: palette.text.primary }}>
            {formatDurationDisplay(race.duration)}
          </Typography>
          <Typography variant="caption" sx={{ color: palette.text.secondary }}>
            Duration
          </Typography>
        </Box>

        {isCoach && onAddTeamRaceLog && teamId ? (
          <Tooltip title="Add Race Logs">
            <IconButton
              size="small"
              onClick={handleAddClick}
              aria-label="Add Race Logs"
              sx={{
                opacity: hovered ? 1 : 0,
                transition: 'opacity 0.15s ease',
                backgroundColor: palette.primary.main,
                color: palette.primary.contrastText,
                width: 30,
                height: 30,
                '&:hover': { backgroundColor: palette.primary.dark },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}

        <ExpandMoreIcon
          sx={{
            color: palette.text.secondary,
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            flexShrink: 0,
          }}
        />
      </Box>

      {teamId && onAddTeamRaceLog ? (
        <CreateTeamRaceLogsModal
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          race={race}
          existingEntries={entries}
          teamId={teamId}
          onSubmit={(payload) => onAddTeamRaceLog(race, payload)}
        />
      ) : null}

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: 2, pb: 2 }}>
          <Box
            sx={{
              padding: 1.5,
              backgroundColor: palette.background.subtle,
              borderRadius: 2,
              border: `1px solid ${palette.grey[200]}`,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: palette.text.primary }}>
              Athlete Results
            </Typography>
            {entriesLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 3,
                  gap: 1,
                }}
              >
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ color: palette.text.secondary }}>
                  Loading race logs…
                </Typography>
              </Box>
            ) : entries.length === 0 ? (
              <Typography variant="body2" sx={{ color: palette.text.secondary, py: 2 }}>
                No Race Logs associated with this event
              </Typography>
            ) : (
              <>
                <Box
                  sx={{
                    ...ROW_GRID,
                    pb: 1,
                    mb: 1,
                    borderBottom: `1px solid ${palette.grey[200]}`,
                  }}
                >
                  <Typography variant="caption" fontWeight={600} sx={{ color: palette.text.secondary }}>
                    Athlete
                  </Typography>
                  <Typography variant="caption" fontWeight={600} sx={{ color: palette.text.secondary }}>
                    Result
                  </Typography>
                  <Typography variant="caption" fontWeight={600} sx={{ color: palette.text.secondary }}>
                    Race type
                  </Typography>
                  <Typography variant="caption" fontWeight={600} sx={{ color: palette.text.secondary }}>
                    Equipment
                  </Typography>
                  <Typography variant="caption" fontWeight={600} sx={{ color: palette.text.secondary }}>
                    Equip. perf.
                  </Typography>
                  <Typography variant="caption" fontWeight={600} sx={{ color: palette.text.secondary }}>
                    Notes
                  </Typography>
                  <Box />
                </Box>
                {entries.map((entry) => (
                  <RaceLogEntryRow
                    key={entry.id}
                    entry={entry}
                    memberId={entry.member_id}
                    onEdit={handleEditEntry}
                    onDelete={handleDeleteEntry}
                  />
                ))}
              </>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default RaceEventCard;
